import React, { useState, useEffect } from 'react';
import { CreditCard, Calendar, Lock, Building, Tag, X } from 'lucide-react';
import { detectCardType, formatCardNumber, isValidExpiry } from '../../services/creditCardService';
import TagSelector from '../Password/TagSelector';

function CreditCardForm({ onSubmit, onCancel, loading = false, editingCard = null, hideHeader = false }) {
  const [formData, setFormData] = useState({
    cardName: '',
    cardNumber: '',
    cardHolder: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: '',
    cardType: '',
    bank: '',
    billingAddress: '',
    tags: [],
    note: '',
    favorite: false
  });

  const [errors, setErrors] = useState({});
  const [cardType, setCardType] = useState('');

  useEffect(() => {
    if (editingCard) {
      setFormData({
        ...editingCard,
        cardNumber: editingCard.cardNumber || '',
        cvv: editingCard.cvv || ''
      });
      setCardType(editingCard.cardType || '');
    }
  }, [editingCard]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    let processedValue = type === 'checkbox' ? checked : value;

    // 카드번호 포맷팅
    if (name === 'cardNumber') {
      const cleaned = value.replace(/\s/g, '');
      if (cleaned.length <= 16) {
        processedValue = formatCardNumber(cleaned, cardType);
        const detectedType = detectCardType(cleaned);
        setCardType(detectedType);
        setFormData(prev => ({ ...prev, cardType: detectedType }));
      } else {
        return;
      }
    }

    // 만료 월/년 포맷팅
    if (name === 'expiryMonth' || name === 'expiryYear') {
      processedValue = value.replace(/\D/g, '').slice(0, 2);
    }

    // CVV 포맷팅
    if (name === 'cvv') {
      processedValue = value.replace(/\D/g, '').slice(0, 4);
    }

    setFormData(prev => ({ ...prev, [name]: processedValue }));
    
    // 에러 클리어
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.cardName.trim()) {
      newErrors.cardName = '카드 이름을 입력해주세요';
    }

    if (!formData.cardNumber) {
      newErrors.cardNumber = '카드번호를 입력해주세요';
    } else if (formData.cardNumber.replace(/\s/g, '').length < 15) {
      newErrors.cardNumber = '올바른 카드번호를 입력해주세요';
    }

    if (!formData.cardHolder.trim()) {
      newErrors.cardHolder = '카드 소유자명을 입력해주세요';
    }

    if (!formData.expiryMonth || !formData.expiryYear) {
      newErrors.expiry = '만료일을 입력해주세요';
    } else if (!isValidExpiry(formData.expiryMonth, formData.expiryYear)) {
      newErrors.expiry = '유효한 만료일을 입력해주세요';
    }

    if (!formData.cvv) {
      newErrors.cvv = 'CVV를 입력해주세요';
    } else if (formData.cvv.length < 3) {
      newErrors.cvv = '올바른 CVV를 입력해주세요';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const getCardTypeIcon = () => {
    const icons = {
      visa: '💳',
      mastercard: '💳',
      amex: '💳',
      discover: '💳'
    };
    return icons[cardType] || '💳';
  };

  return (
    <form onSubmit={handleSubmit} className="credit-card-form">
      {!hideHeader && (
        <div className="form-header">
          <h3>{editingCard ? '신용카드 수정' : '신용카드 추가'}</h3>
          <button type="button" onClick={onCancel} className="btn-close">
            <X size={20} />
          </button>
        </div>
      )}

      <div className="form-body">
        {/* 카드 이름 */}
        <div className="form-group">
          <label htmlFor="cardName">
            <CreditCard size={16} /> 카드 이름 *
          </label>
          <input
            type="text"
            id="cardName"
            name="cardName"
            value={formData.cardName}
            onChange={handleInputChange}
            placeholder="예: 개인 비자카드"
            className={errors.cardName ? 'error' : ''}
            autoFocus
          />
          {errors.cardName && <span className="error-message">{errors.cardName}</span>}
        </div>

        {/* 카드번호 */}
        <div className="form-group">
          <label htmlFor="cardNumber">
            {getCardTypeIcon()} 카드번호 *
          </label>
          <div className="input-with-type">
            <input
              type="text"
              id="cardNumber"
              name="cardNumber"
              value={formData.cardNumber}
              onChange={handleInputChange}
              placeholder="1234 5678 9012 3456"
              className={errors.cardNumber ? 'error' : ''}
            />
            {cardType && (
              <span className={`card-type card-type-${cardType}`}>
                {cardType.toUpperCase()}
              </span>
            )}
          </div>
          {errors.cardNumber && <span className="error-message">{errors.cardNumber}</span>}
        </div>

        {/* 카드 소유자명 */}
        <div className="form-group">
          <label htmlFor="cardHolder">
            카드 소유자명 *
          </label>
          <input
            type="text"
            id="cardHolder"
            name="cardHolder"
            value={formData.cardHolder}
            onChange={handleInputChange}
            placeholder="홍길동"
            className={errors.cardHolder ? 'error' : ''}
          />
          {errors.cardHolder && <span className="error-message">{errors.cardHolder}</span>}
        </div>

        {/* 만료일 & CVV */}
        <div className="form-row">
          <div className="form-group">
            <label>
              <Calendar size={16} /> 만료일 *
            </label>
            <div className="expiry-inputs">
              <input
                type="text"
                name="expiryMonth"
                value={formData.expiryMonth}
                onChange={handleInputChange}
                placeholder="MM"
                maxLength="2"
                className={errors.expiry ? 'error' : ''}
              />
              <span>/</span>
              <input
                type="text"
                name="expiryYear"
                value={formData.expiryYear}
                onChange={handleInputChange}
                placeholder="YY"
                maxLength="2"
                className={errors.expiry ? 'error' : ''}
              />
            </div>
            {errors.expiry && <span className="error-message">{errors.expiry}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="cvv">
              <Lock size={16} /> CVV *
            </label>
            <input
              type="text"
              id="cvv"
              name="cvv"
              value={formData.cvv}
              onChange={handleInputChange}
              placeholder="123"
              maxLength="4"
              className={errors.cvv ? 'error' : ''}
            />
            {errors.cvv && <span className="error-message">{errors.cvv}</span>}
          </div>
        </div>

        {/* 은행 */}
        <div className="form-group">
          <label htmlFor="bank">
            <Building size={16} /> 발급 은행
          </label>
          <input
            type="text"
            id="bank"
            name="bank"
            value={formData.bank}
            onChange={handleInputChange}
            placeholder="예: 신한은행"
          />
        </div>

        {/* 청구지 주소 */}
        <div className="form-group">
          <label htmlFor="billingAddress">
            청구지 주소
          </label>
          <textarea
            id="billingAddress"
            name="billingAddress"
            value={formData.billingAddress}
            onChange={handleInputChange}
            placeholder="청구지 주소 입력"
            rows="2"
          />
        </div>

        {/* 태그 */}
        <div className="form-group">
          <label>
            <Tag size={16} /> 태그
          </label>
          <TagSelector
            selectedTags={formData.tags}
            onChange={(tags) => setFormData(prev => ({ ...prev, tags }))}
            placeholder="태그 추가 (예: 개인, 업무)"
          />
        </div>

        {/* 메모 */}
        <div className="form-group">
          <label htmlFor="note">
            메모
          </label>
          <textarea
            id="note"
            name="note"
            value={formData.note}
            onChange={handleInputChange}
            placeholder="메모 입력"
            rows="3"
          />
        </div>

        {/* 즐겨찾기 */}
        <div className="form-group checkbox-group">
          <label>
            <input
              type="checkbox"
              name="favorite"
              checked={formData.favorite}
              onChange={handleInputChange}
            />
            즐겨찾기에 추가
          </label>
        </div>
      </div>

      <div className="form-footer">
        <button type="button" onClick={onCancel} className="btn btn-secondary">
          취소
        </button>
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? '저장 중...' : (editingCard ? '수정' : '추가')}
        </button>
      </div>
    </form>
  );
}

export default CreditCardForm;